/**
 * The give box's state that is not drawing (GH-847): its session, its quote, its height, and the
 * wait for the processor after the donor authorizes.
 *
 * Every figure here is the server's. The box sends what the donor chose — an amount, a fund,
 * whether to cover the fee — and renders the quote that comes back; it never adds a fee, never
 * totals anything, and never treats a browser callback as money received (SPEC.md §5.4).
 */
import type {
  ContextResponse,
  DonationStatus,
  EmbedSessionWidget,
  QuoteResponse,
} from "@givehere/contracts";
import { useEffect, useRef, useState } from "react";
import {
  ApiError,
  createQuote,
  enrichReceipt,
  saveHeldReceiptEmail,
  fetchContext,
  fetchDonationStatus,
  mintEmbedSession,
} from "../api.js";
import { postCompletion, postHeight } from "./embed-frame.js";

/** Re-mint this long before the session's hour runs out, so an open box never holds a dead token. */
const REMINT_EARLY_MS = 2 * 60 * 1000;

export type EmbedSession =
  | { readonly state: "loading" }
  | { readonly state: "failed"; readonly message: string }
  /** An archived saved widget (GH-851): no context; the box points at the organization's page. */
  | {
      readonly state: "unavailable";
      readonly organizationName: string;
      readonly brandColor: string | null;
    }
  | {
      readonly state: "ready";
      readonly token: string;
      readonly context: ContextResponse;
      /** The organization's `#rrggbb`, or null: the keyline's tint when the address names none. */
      readonly brandColor: string | null;
      /** The saved widget or draft preview the server says to draw (GH-851); null for defaults. */
      readonly widget: EmbedSessionWidget | null;
    };

/** What the box asks the mint for: the snippet's event, its saved widget, the console's draft. */
export type EmbedSessionRequestHints = {
  readonly eventId: string | null;
  readonly widgetId?: string | null;
  readonly previewToken?: string | null;
};

/**
 * Mint the box's checkout context once it has loaded, and again shortly before it expires (or
 * when the console hands its preview a new draft). The token lives in this component's memory
 * only — never in the address, never in storage.
 */
export function useEmbedSession(
  organizationId: string,
  hints: EmbedSessionRequestHints,
): EmbedSession {
  const [session, setSession] = useState<EmbedSession>({ state: "loading" });
  const { eventId, widgetId = null, previewToken = null } = hints;

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const mint = () => {
      mintEmbedSession({
        organizationId,
        ...(eventId ? { eventId } : {}),
        ...(widgetId ? { widgetId } : {}),
        ...(previewToken ? { previewToken } : {}),
      })
        .then(async (minted) => {
          if (minted.state === "unavailable") {
            if (!cancelled) {
              setSession({
                state: "unavailable",
                organizationName: minted.organizationName,
                brandColor: minted.brandColor,
              });
            }
            return;
          }
          const context = await fetchContext(minted.token);
          if (cancelled) return;
          setSession({
            state: "ready",
            token: minted.token,
            context,
            brandColor: minted.brandColor,
            widget: minted.widget,
          });
          const remintIn = Date.parse(minted.expiresAt) - Date.now() - REMINT_EARLY_MS;
          if (Number.isFinite(remintIn) && remintIn > 0) timer = setTimeout(mint, remintIn);
        })
        .catch((caught: unknown) => {
          if (cancelled) return;
          setSession({
            state: "failed",
            message:
              caught instanceof ApiError ? caught.message : "This give box could not be loaded.",
          });
        });
    };
    mint();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [organizationId, eventId, widgetId, previewToken]);

  return session;
}

export type QuoteState = {
  readonly quote: QuoteResponse | null;
  readonly quoting: boolean;
  readonly error: string | null;
};

/**
 * The server's price for what the donor has chosen, re-asked whenever a choice changes. A quote is
 * not a charge: nothing is paid until the donor presses a payment button and authorizes.
 */
export function useEmbedQuote(input: {
  readonly token: string | null;
  readonly canQuote: boolean;
  readonly amountMinor: string | null;
  readonly costSupport: boolean;
  readonly designationId: string | null;
  /** GH-877: a signed-in donor's session, sent with the quote as the give page sends it. */
  readonly donorToken?: string | null;
}): QuoteState {
  const { token, canQuote, amountMinor, costSupport, designationId, donorToken = null } = input;
  const [state, setState] = useState<QuoteState>({ quote: null, quoting: false, error: null });

  useEffect(() => {
    if (!token || !canQuote || !amountMinor) {
      setState({ quote: null, quoting: false, error: null });
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      setState((previous) => ({ ...previous, quoting: true }));
      const body = {
        contextToken: token,
        amountMinor,
        methodClass: "card" as const,
        costSupportSelected: costSupport,
        recognition: { mode: "anonymous" as const },
        designationId,
      };
      // A guest's quote is exactly what it always was; a signed-in donor's carries the session.
      (donorToken ? createQuote(body, donorToken) : createQuote(body))
        .then((quote) => {
          if (!cancelled) setState({ quote, quoting: false, error: null });
        })
        .catch((caught: unknown) => {
          if (cancelled) return;
          setState({
            quote: null,
            quoting: false,
            error: caught instanceof ApiError ? caught.message : "Could not calculate the total.",
          });
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [token, canQuote, amountMinor, costSupport, designationId, donorToken]);

  return state;
}

/**
 * Tell the framing page how tall the box is whenever that changes. `floor` keeps the frame tall
 * enough for an overlay — Stripe's card dialog and its 3-D Secure challenge are fixed-position
 * inside this frame, so a frame shrunk to the form would clip them.
 */
export function useHostHeight(floor: number): void {
  const floorRef = useRef(floor);
  floorRef.current = floor;

  useEffect(() => {
    const report = () =>
      postHeight(Math.max(floorRef.current, document.documentElement.scrollHeight));
    report();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(report);
    observer.observe(document.body);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    postHeight(Math.max(floor, document.documentElement.scrollHeight));
  }, [floor]);
}

const POLL_MS = 2500;
/** How long the box waits for the processor before saying "processing" and stopping. */
const POLL_LIMIT = 24;

export type Completion = {
  readonly status: DonationStatus | null;
  /** True once the receipt went to the address the donor typed. */
  readonly receiptSent: boolean;
  /** GH-947: a held gift's address was kept; its receipt is emailed when the gift is charged. */
  readonly receiptAwaitingCharge: boolean;
};

/**
 * After the donor authorizes: wait for the server to say the gift was received (only a verified
 * processor callback makes it so), send the receipt to the address they typed, and tell the host
 * page once — status and amount, nothing else.
 */
export function useCompletion(
  paid: { readonly donationId: string; readonly accessToken: string } | null,
  email: string,
): Completion {
  const [status, setStatus] = useState<DonationStatus | null>(null);
  const [receiptSent, setReceiptSent] = useState(false);
  const [receiptAwaitingCharge, setReceiptAwaitingCharge] = useState(false);
  const emailRef = useRef(email);
  emailRef.current = email;

  useEffect(() => {
    if (!paid) return;
    let cancelled = false;
    let polls = 0;
    let told = false;

    const tell = (latest: DonationStatus) => {
      if (told) return;
      told = true;
      postCompletion(latest.displayStatus === "received" ? "received" : "processing", latest.total);
    };

    const poll = async () => {
      if (cancelled) return;
      polls += 1;
      try {
        const latest = await fetchDonationStatus(paid.donationId, paid.accessToken);
        if (cancelled) return;
        setStatus(latest);
        if (latest.displayStatus === "received" && latest.receipt) {
          tell(latest);
          const address = emailRef.current.trim();
          if (address) {
            await enrichReceipt(latest.receipt.receiptId, paid.accessToken, { email: address })
              .then(() => {
                if (!cancelled) setReceiptSent(true);
              })
              // The gift stands either way; the receipt is still on screen.
              .catch(() => undefined);
          }
          return;
        }
        if (latest.displayStatus === "failed") return;
        // DEC-056: approved, charged at the organization's end of day — nothing more to wait for.
        if (latest.heldForEndOfDay) {
          tell(latest);
          // GH-947: no receipt exists until the day's end; the server keeps the address for it.
          const address = emailRef.current.trim();
          if (address) {
            await saveHeldReceiptEmail(paid.donationId, paid.accessToken, { email: address })
              .then(() => {
                if (!cancelled) setReceiptAwaitingCharge(true);
              })
              .catch(() => undefined);
          }
          return;
        }
        if (polls >= POLL_LIMIT) {
          tell(latest);
          return;
        }
      } catch {
        if (polls >= POLL_LIMIT) return;
      }
      setTimeout(() => void poll(), POLL_MS);
    };
    void poll();

    return () => {
      cancelled = true;
    };
  }, [paid]);

  return { status, receiptSent, receiptAwaitingCharge };
}
